import ApiService from './api-service';

const ImportService = {
  /**
   * Retrieves the CSV mapping configuration for an organisation.
   *
   * @param {string} shortCode - The short code of the organisation.
   * @return {object} The CSV mapping configuration.
   */
  async getCSVMapping(shortCode) {
    const body = {
      query: /* GraphQL */ `
        query {
          organisation {
            _id
            ShortCode

            csvMapping {
              configuration {
                fieldSeparator
                dateFormat
                moneyDecimalSeparator
              }
              types {
                id
                fields {
                  id
                  value {
                    __typename

                    ... on CSVMappingFieldValueAdditionalContacts {
                      numberOfContacts
                      contacts {
                        _id
                        firstNameColumnId
                        lastNameColumnId
                        splitFromFirstName
                        emailAddressColumnId
                        statusColumnId
                      }
                      statuses {
                        chaserStatus
                        csvStatuses {
                          csvStatus
                        }
                      }
                    }

                    ... on CSVMappingFieldValueLastName {
                      optionalColumnId: columnId
                      splitFromFirstName
                    }

                    ... on CSVMappingFieldValueMoney {
                      columnId
                      decimalSeparator
                    }

                    ... on CSVMappingFieldValueDate {
                      columnId
                      format
                    }

                    ... on CSVMappingFieldValueSimple {
                      columnId
                    }

                    ... on CSVMappingFieldValueStatus {
                      columnId
                      statuses {
                        chaserStatus
                        csvStatuses {
                          csvStatus
                        }
                      }
                    }

                    ... on CSVMappingFieldValueTags {
                      mapping {
                        __typename
                        ... on CSVMappingFieldValueTagsMappingMultipleColumns {
                          columns {
                            columnId
                          }
                        }
                        ... on CSVMappingFieldValueTagsMappingSingleColumn {
                          columnId
                          separator
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `,
    };
    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    return data.organisation.csvMapping;
  },

  /**
   * Retrieves the CSV mapping specification.
   *
   * @param {string} shortCode - The short code of the organization.
   * @return {object} The CSV mapping specification.
   */
  async getCSVMappingSpecification(shortCode) {
    const body = {
      query: /* GraphQL */ `
        fragment SpecificationFields on CSVMappingSpecificationTypeField {
          id
          name
          required
          component

          description {
            type
            content
          }

          statuses {
            id
            name
            required

            description {
              type
              content
            }
          }
        }

        query {
          csv {
            mappingSpecification {
              maxCsvSize
              maxPdfSize

              supportedDateFormats {
                dateFnsFormat
              }

              types {
                id
                name

                fields {
                  ...SpecificationFields

                  fields {
                    ...SpecificationFields
                  }
                }
              }
            }
          }
        }
      `,
    };
    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    return data.csv.mappingSpecification;
  },

  /**
   * Retrieves CSV upload links for the specified names.
   *
   * @param {string} shortCode - The short code for the organization.
   * @param {string[]} names - The names for which to retrieve CSV upload links.
   * @return {Promise<{urls: {
   *    [name: string]: string
   * }, headers: {
   *    [name: string]: string
   * }, prefix: string, invalidFileNames: string[]|null}>} An object containing the CSV upload links, required upload headers, upload identifier, and invalid file names.
   */
  async getCSVUploadLinks(shortCode, names) {
    const body = {
      query: /* GraphQL */ `
        query($names: [String!]!) {
          csv {
            uploadLinks(names: $names) {
              urls
              headers
              prefix
              invalidFileNames
            }
          }
        }
      `,
      variables: {
        names,
      },
    };
    const data = await ApiService.orgEndpoint(shortCode).post('/', body);

    return data.csv.uploadLinks;
  },

  /**
   * Retrieves PDF upload links for the specified names.
   *
   * @param {string} shortCode - The short code for the organization.
   * @param {string[]} names - The names for which to retrieve PDF upload links.
   * @return {Promise<{urls: {
   *    [name: string]: string
   * }, headers: {
   *    [name: string]: string
   * }, prefix: string, invalidFileNames: string[]|null}>} An object containing the PDF upload links, required upload headers, upload identifier, and invalid file names.
   */
  async getPDFUploadLinks(shortCode, names) {
    const body = {
      query: /* GraphQL */ `
        query($names: [String!]!) {
          csv {
            pdfUploadLinks(names: $names) {
              urls
              headers
              prefix
              invalidFileNames
            }
          }
        }
      `,
      variables: {
        names,
      },
    };
    const data = await ApiService.orgEndpoint(shortCode).post('/', body);

    return data.csv.pdfUploadLinks;
  },

  /**
   * Reads a CSV file from the server.
   *
   * @param {string} shortCode - The short code of the organization.
   * @param {string} prefix - The prefix of the CSV file.
   * @param {string} name - The name of the CSV file.
   * @return {Promise<Object[]>} The contents of the CSV file.
   */
  async readCSV(shortCode, prefix, name) {
    const body = {
      query: /* GraphQL */ `
        query($prefix: String!, $name: String!) {
          csv {
            readFile(prefix: $prefix, name: $name) {
              rows
            }
          }
        }
      `,
      variables: {
        prefix,
        name,
      },
    };
    const data = await ApiService.orgEndpoint(shortCode).post('/', body);

    return data.csv.readFile.rows;
  },

  /**
   * Imports a CSV file. This encompasses the parsing and validation of the file,
   * the import of the data into the database and deletion of the temporary file from the bucket.
   *
   * @param {string} shortCode - The short code of the organization.
   * @param {string} prefix - Upload identifier.
   * @param {string} name - The name of the CSV file.
   * @param {string} type - The type of the CSV file.
   * @return {Promise<{refresh: boolean}>} A promise that resolves to the result of the import.
   */
  async importCSV(shortCode, prefix, name, type) {
    const body = {
      query: /* GraphQL */ `
        mutation($prefix: String!, $name: String!, $type: String!) {
          csv {
            importFile(prefix: $prefix, name: $name, type: $type) {
              refresh
              skippedCount
              skipped {
                invoiceID
                invoiceNumber
                contactID
              }
            }
          }
        }
      `,
      variables: {
        prefix,
        name,
        type,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    return data.csv.importFile;
  },

  /**
   * Imports one or more PDF files. This encompasses the matching of the PDF file to the invoice,
   * PDF processing and deletion of the temporary files from the bucket.
   *
   * @param {string} shortCode - The short code of the organization.
   * @param {string} prefix - Upload identifier.
   * @param {string[]} names - The names of the PDF files.
   * @return {Promise<{refresh: boolean}>} A promise that resolves to the result of the import.
   */
  async importPDFs(shortCode, prefix, names) {
    const body = {
      query: /* GraphQL */ `
        mutation($prefix: String!, $names: [String!]!) {
          csv {
            importPDFs(prefix: $prefix, names: $names) {
              refresh
            }
          }
        }
      `,
      variables: {
        prefix,
        names,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);

    return data.csv.importPDFs;
  },

  async saveCSVMappingConfiguration(shortCode, configuration, prefix, name) {
    const body = {
      query: /* GraphQL */ `
        mutation($csvMapping: CSVMappingInput!, $prefix: String, $name: String) {
          csv {
            saveConfiguration(csvMapping: $csvMapping, prefix: $prefix, name: $name)
          }
        }
      `,
      variables: {
        csvMapping: configuration,
        prefix,
        name,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    return data.csv.saveConfiguration;
  },
};

export default ImportService;
